import { useState, useEffect, useRef } from "react";
import { highlightText } from "@speed-highlight/core";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileJson,
  Loader2,
  Package,
  Box,
  ExternalLink,
  Globe,
  RefreshCw,
} from "lucide-react";
import { useUrlState } from "@/hooks/useUrlState";
import { getProduct, getProductByVariant } from "@/services/productsApi";
import { scrapeProductById } from "@/services/scrapersApi";
import { API_ENDPOINTS } from "@/config/api";
import type { ProductViewerUrlState } from "@/types/pageState";
import { Toaster, toast } from "sonner";
import { CopyJsonButton } from "@/components/ui/json-block";

const URL_REGEX = /\bhttps?:\/\/[^\s"'<]+/g;

/** Extract product ID from variant/product viewer JSON (checks common paths in API response). */
function getProductIdFromViewerData(data: unknown): number | undefined {
  if (data == null || typeof data !== "object") return undefined;
  const o = data as Record<string, unknown>;
  const from = (v: unknown): number | undefined => {
    if (typeof v === "number" && Number.isInteger(v) && v > 0) return v;
    if (typeof v === "string") {
      const n = parseInt(v, 10);
      return Number.isInteger(n) && n > 0 ? n : undefined;
    }
    return undefined;
  };
  const details = o.details as Record<string, unknown> | undefined;
  const tried = [
    o.product_id,
    o.productId,
    details?.id,
    details?.product_id,
    (o.product as Record<string, unknown> | undefined)?.id,
    (o.product as Record<string, unknown> | undefined)?.product_id,
    (o.product as Record<string, unknown> | undefined)?.productId,
  ];
  for (const v of tried) {
    const id = from(v);
    if (id != null) return id;
  }
  return undefined;
}

function linkifyHighlightedJson(html: string): string {
  return html.replace(URL_REGEX, (url) => {
    const href = url.replace(/"/g, "&quot;");
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="underline text-primary hover:text-primary/80">${url}</a>`;
  });
}

export default function Viewer() {
  const [urlState, setUrlState] = useUrlState<ProductViewerUrlState>({
    schema: {
      type: "string",
      id: "number",
    },
    defaults: {},
    replace: true,
  });

  const type = urlState.type ?? "product";
  const id = urlState.id;

  const [data, setData] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedHtml, setHighlightedHtml] = useState<string | null>(null);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [isScraping, setIsScraping] = useState(false);
  const highlightRef = useRef<{ type: string; id: number } | null>(null);
  const jsonScrollRef = useRef<HTMLDivElement | null>(null);

  const viewHtmlUrl =
    id != null && id > 0
      ? type === "product"
        ? API_ENDPOINTS.scrapersProductView(id)
        : API_ENDPOINTS.scrapersVariantView(id)
      : null;

  // Fetch when type and id are set
  useEffect(() => {
    if (id == null || id <= 0) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    const isProduct = type === "product";
    const fetchFn = isProduct
      ? () => getProduct(id)
      : () => getProductByVariant(id);
    fetchFn()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [type, id]);

  // JSON syntax highlighting when data is available
  useEffect(() => {
    if (data == null || id == null) {
      setHighlightedHtml(null);
      return;
    }
    const rawText =
      typeof data === "string" ? data : JSON.stringify(data, null, 2);
    highlightRef.current = { type, id };
    setHighlightedHtml(null);
    highlightText(rawText, "json", false)
      .then((html) => {
        if (
          highlightRef.current?.type === type &&
          highlightRef.current?.id === id
        ) {
          setHighlightedHtml(linkifyHighlightedJson(html));
        }
      })
      .catch(() => {
        if (
          highlightRef.current?.type === type &&
          highlightRef.current?.id === id
        ) {
          setHighlightedHtml(null);
        }
      });
    return () => {
      highlightRef.current = null;
    };
  }, [data, type, id]);

  // Auto-show HTML viewer when product/variant data is loaded
  useEffect(() => {
    if (data != null && viewHtmlUrl) {
      setShowHtmlPreview(true);
    }
  }, [data, viewHtmlUrl]);

  // When viewing a variant, scroll the JSON pane to the variant id so it's visible
  useEffect(() => {
    if (
      type !== "variant" ||
      id == null ||
      highlightedHtml == null ||
      !jsonScrollRef.current
    )
      return;
    const idStr = String(id);
    const scroll = () => {
      const container = jsonScrollRef.current;
      if (!container) return;
      const applyScroll = (el: Element) => {
        el.scrollIntoView({ block: "start", behavior: "auto" });
        const content =
          container.querySelector('[class*="shj-lang"]') ??
          container.firstElementChild;
        const lineHeight = content
          ? parseFloat(getComputedStyle(content as Element).lineHeight)
          : 20;
        container.scrollTop = Math.max(
          0,
          container.scrollTop - (lineHeight || 20),
        );
      };
      // speed-highlight uses .shj-syn-num for numbers; find the one that is the variant id in "id" context
      const numSpans = container.querySelectorAll('[class*="shj-syn-num"]');
      for (const el of numSpans) {
        if (el.textContent?.trim() !== idStr) continue;
        // Prefer a number that is the value of "id" (e.g. "id": 123) by checking preceding content
        const prev = el.previousElementSibling?.textContent ?? "";
        if (
          prev.includes('"id"') ||
          prev.includes(":") ||
          el.previousElementSibling?.previousElementSibling?.textContent?.includes(
            "id",
          )
        ) {
          applyScroll(el);
          return;
        }
      }
      // Fallback: scroll to first occurrence of this number
      for (const el of numSpans) {
        if (el.textContent?.trim() === idStr) {
          applyScroll(el);
          return;
        }
      }
    };
    const t = requestAnimationFrame(() => requestAnimationFrame(scroll));
    return () => cancelAnimationFrame(t);
  }, [type, id, highlightedHtml]);

  const handleTypeChange = (value: string) => {
    setUrlState((prev) => ({
      ...prev,
      type: value === "variant" ? "variant" : "product",
    }));
  };

  const handleIdChange = (value: string) => {
    const num = value.trim() ? parseInt(value, 10) : undefined;
    setUrlState((prev) => ({
      ...prev,
      id: num !== undefined && !Number.isNaN(num) ? num : undefined,
    }));
  };

  const handleView = () => {
    const input = document.getElementById("product-viewer-id") as
      | HTMLInputElement
      | undefined;
    const value = input?.value?.trim();
    const num = value ? parseInt(value, 10) : undefined;
    if (num != null && !Number.isNaN(num)) {
      setUrlState((prev) => ({ ...prev, id: num }));
    }
  };

  /** Product ID to use for scraping: for product view it's id; for variant view it's from loaded JSON. */
  const scrapeProductId =
    type === "product" && id != null && id > 0
      ? id
      : type === "variant" && data != null
        ? getProductIdFromViewerData(data)
        : undefined;

  const handleScrapeProduct = async () => {
    const productId = type === "product" ? id : scrapeProductId;
    if (productId == null || productId <= 0) return;
    setIsScraping(true);
    try {
      await scrapeProductById(productId, {
        onStatus: (status) => {
          if (status === "queued") toast.info("Scrape queued…");
          if (status === "in_progress") toast.info("Scrape in progress…");
        },
      });
      toast.success("Product scraped successfully");
    } catch (err) {
      // A 401 never reaches here: the session-expired handler unmounts this
      // page and redirects to login before a toast could show.
      toast.error(err instanceof Error ? err.message : "Scrape failed");
    } finally {
      setIsScraping(false);
    }
  };

  const idInputValue = id != null ? String(id) : "";
  const canScrapeProduct =
    !loading &&
    viewHtmlUrl != null &&
    scrapeProductId != null &&
    scrapeProductId > 0;

  return (
    <>
      <Toaster position="top-right" theme="dark" />
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Product Viewer</h2>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileJson className="w-5 h-5" />
              View product or variant data
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Enter a product ID or variant ID to view its data from the API
              (GET /api/products/{"{product_id}"} or /api/products/by-variant/
              {"{variant_id}"}).
            </p>
            {viewHtmlUrl && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {canScrapeProduct && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleScrapeProduct}
                    disabled={isScraping}
                  >
                    {isScraping ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    {isScraping ? "Scraping…" : "Scrape product"}
                  </Button>
                )}
                <Button variant="outline" size="sm" asChild>
                  <a
                    href={viewHtmlUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View scraped page
                  </a>
                </Button>
                <Button
                  variant={showHtmlPreview ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setShowHtmlPreview((p) => !p)}
                >
                  <Globe className="w-4 h-4 mr-2" />
                  {showHtmlPreview ? "Hide" : "Show"} embedded preview
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2">
                <Label htmlFor="product-viewer-type">Type</Label>
                <Select value={type} onValueChange={handleTypeChange}>
                  <SelectTrigger id="product-viewer-type" className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">
                      <span className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Product
                      </span>
                    </SelectItem>
                    <SelectItem value="variant">
                      <span className="flex items-center gap-2">
                        <Box className="w-4 h-4" />
                        Variant
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="product-viewer-id">ID</Label>
                <Input
                  id="product-viewer-id"
                  type="number"
                  min={1}
                  placeholder={type === "product" ? "Product ID" : "Variant ID"}
                  value={idInputValue}
                  onChange={(e) => handleIdChange(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleView()}
                  className="w-[160px]"
                />
              </div>
              <Button onClick={handleView} disabled={!idInputValue || loading}>
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "View"
                )}
              </Button>
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}

            {loading && (
              <div className="flex items-center gap-2 text-muted-foreground py-8">
                <Loader2 className="w-5 h-5 animate-spin" />
                Loading…
              </div>
            )}

            {!loading && !error && data != null && id != null && (
              <div
                className={
                  showHtmlPreview && viewHtmlUrl
                    ? "grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch"
                    : "space-y-4"
                }
              >
                <div className="rounded-md border bg-muted/30 overflow-hidden min-w-0 flex flex-col">
                  <div className="px-3 py-2 border-b bg-muted/50 text-sm font-medium font-mono flex items-center justify-between">
                    <span>
                      {type === "product" ? "Product" : "Variant"} {id}
                    </span>
                    <CopyJsonButton value={data} />
                  </div>
                  <div
                    ref={jsonScrollRef}
                    className="p-3 min-h-[200px] max-h-[70vh] overflow-auto rounded-b-md border-t-0 bg-background flex-1"
                  >
                    <div className="text-xs font-mono whitespace-pre-wrap wrap-break-word">
                      {highlightedHtml !== null ? (
                        <div
                          className="shj-lang-json bg-background! text-foreground! p-0! m-0! shadow-none! min-w-0!"
                          dangerouslySetInnerHTML={{ __html: highlightedHtml }}
                        />
                      ) : (
                        <pre className="m-0 p-0 bg-transparent">
                          {typeof data === "string"
                            ? data
                            : JSON.stringify(data, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                </div>

                {showHtmlPreview && viewHtmlUrl && (
                  <div className="rounded-md border bg-muted/30 overflow-hidden min-w-0 flex flex-col min-h-[300px]">
                    <div className="px-3 py-2 border-b bg-muted/50 text-sm font-medium flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Scraped page preview
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 ml-auto shrink-0"
                        title="Open preview in new tab"
                        asChild
                      >
                        <a
                          href={viewHtmlUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                    <div className="border-t-0 bg-background flex-1 flex flex-col min-h-0">
                      <iframe
                        title={`Scraped ${type} ${id}`}
                        src={viewHtmlUrl}
                        className="w-full flex-1 min-h-[60vh] md:min-h-0 border-0 rounded-b-md"
                        sandbox="allow-same-origin allow-scripts"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {!loading && !error && (id == null || id <= 0) && (
              <p className="text-muted-foreground text-sm py-6">
                Select type and enter an ID, then click View.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
